import { Divider, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { calculateTimesheetWorkedHours } from "../utils/timesheetFormatUtils";
import { TimesheetTimeEntryRow } from "./TimesheetTimeEntryRow";

interface ReadOnlyTimesheetTimeEntriesProps {
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  formatBreakDisplay: () => string;
  /** Pass the original time when clock-in has been adjusted from the recorded value */
  originalClockInTime?: Date;
  /** Pass the original time when clock-out has been adjusted from the recorded value */
  originalClockOutTime?: Date;
  /** Pass the original formatted break string when break has been adjusted */
  originalBreakDisplay?: string;
}

export function ReadOnlyTimesheetTimeEntries(props: ReadOnlyTimesheetTimeEntriesProps) {
  const {
    displayTimesheet,
    workplaceTimezone,
    formatBreakDisplay,
    originalClockInTime,
    originalClockOutTime,
    originalBreakDisplay,
  } = props;

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  return (
    <Stack spacing={4}>
      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock in"
        originalValue={originalClockInTime ? formatWorkplaceTime(originalClockInTime) : undefined}
      >
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Text>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow iconType="break" label="Break" originalValue={originalBreakDisplay}>
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatBreakDisplay()}
        </Text>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock out"
        originalValue={originalClockOutTime ? formatWorkplaceTime(originalClockOutTime) : undefined}
      >
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatWorkplaceTime(displayTimesheet.clockOutTime)}
        </Text>
      </TimesheetTimeEntryRow>

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }}>
          Total
        </Text>
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.success.main }}>
          {formatHoursDurationCompact(calculateTimesheetWorkedHours(displayTimesheet))}
        </Text>
      </Stack>
    </Stack>
  );
}
